import type { TiptapDoc } from '@odo/shared-ui';
import {
    draftFromStored,
    draftToDraftPlan,
    draftToPlan,
    hasDraftErrors,
    isDescriptionOverCap,
    validateDraft,
    type VirtualAppointmentDraft,
    type VirtualAppointmentDraftErrors
} from '../../../virtual-appointments/virtualAppointmentDraft';
import type {
    VirtualAppointmentDraftPlan,
    VirtualAppointmentDto,
    VirtualAppointmentPlan
} from '../../../../services/virtualAppointments';
import { legacyNoteToDraft } from './legacyNoteConversion';
import type { VirtualAppointmentLoadStatus } from '@odo/shared-ui/clinical/virtualAppointments/types';

export type { VirtualAppointmentLoadStatus };

export type SubmitPlansResult =
    { ok: true; plans: VirtualAppointmentPlan[] } | { ok: false; blockedHint: string };

const LOADING_HINT = 'Loading existing VAs…';
const LOAD_FAILED_HINT = "Couldn't load this document's VAs - retry on the VAs step";

// What a resumed draft carried; each field undefined when the draft never had it.
export interface ResumedDraftPlans {
    plans: VirtualAppointmentDraftPlan[] | undefined;
    /** Planned VA ids the draft's author had loaded; absent on older drafts. */
    seenIds: string[] | undefined;
}

export interface HydratePlansInput {
    draft: ResumedDraftPlans;
    /** Pre-VA free text from the resumed draft or edited event; converted into a new VA. */
    legacyNote: string;
    /** The document group's VAs; null when there is no group at all. */
    server: VirtualAppointmentDto[] | null;
}

export interface HydratedPlans {
    planned: VirtualAppointmentDraft[];
    frozen: VirtualAppointmentDto[];
    /** Draft rows whose VA no longer exists on the server; dropped, surfaced as a notice. */
    removedElsewhere: VirtualAppointmentDraft[];
    /** The planned server ids this session loaded; the next draft save records exactly these. */
    seenIds: string[];
    /** Key of the VA created from the legacy note, if there was one. */
    convertedLegacyKey: string | null;
}

type MergedSources = Omit<HydratedPlans, 'convertedLegacyKey'>;

export interface DraftPlanFields {
    virtualAppointments?: VirtualAppointmentDraftPlan[];
    virtualAppointmentsSeenIds?: string[];
}

function withoutId(plan: VirtualAppointmentDraftPlan): VirtualAppointmentDraftPlan {
    const { id: _id, ...rest } = plan;
    return rest;
}

interface DraftMergeResult {
    planned: VirtualAppointmentDraft[];
    removedElsewhere: VirtualAppointmentDraft[];
}

// Frozen on the server wins; a draft row wins over its planned server row; a planned server
// row the draft lacks was removed by the user only if its id was seen, else it is new.
function mergeDraftWithServer(
    draft: ResumedDraftPlans & { plans: VirtualAppointmentDraftPlan[] },
    plannedServer: VirtualAppointmentDto[],
    frozen: VirtualAppointmentDto[]
): DraftMergeResult {
    const frozenIds = new Set(frozen.map((va) => va.id));
    const plannedById = new Map(plannedServer.map((va) => [va.id, va]));
    const inDraft = new Set<string>();
    const removedElsewhere: VirtualAppointmentDraft[] = [];

    const fromDraft = draft.plans.flatMap((plan) => {
        const id = plan.id ?? undefined;
        if (id === undefined) return [draftFromStored(plan)];
        if (frozenIds.has(id) || inDraft.has(id)) return [];
        inDraft.add(id);
        const serverRow = plannedById.get(id);
        // Drafts saved before weeksFrom existed take the saved anchor instead.
        if (serverRow) {
            return [draftFromStored({ triggerWeeksFrom: serverRow.triggerWeeksFrom, ...plan })];
        }
        removedElsewhere.push(draftFromStored(withoutId(plan)));
        return [];
    });
    const removedByUser = new Set(draft.seenIds ?? []);
    const addedSinceDraft = plannedServer
        .filter((va) => !inDraft.has(va.id) && !removedByUser.has(va.id))
        .map(draftFromStored);
    return { planned: [...fromDraft, ...addedSinceDraft], removedElsewhere };
}

function mergeSources({ draft, server }: Omit<HydratePlansInput, 'legacyNote'>): MergedSources {
    if (server === null) {
        // No group means no rows yet, so no id can match; every row is new.
        const planned = (draft.plans ?? []).map((p) => draftFromStored(withoutId(p)));
        return { planned, frozen: [], removedElsewhere: [], seenIds: [] };
    }
    const frozen = server.filter((va) => va.frozen);
    const plannedServer = server.filter((va) => !va.frozen);
    // Not unioned with the carried ids: a VA frozen since then and later reopened must read as new.
    const seenIds = plannedServer.map((va) => va.id);
    if (draft.plans === undefined) {
        return {
            planned: plannedServer.map(draftFromStored),
            frozen,
            removedElsewhere: [],
            seenIds
        };
    }
    const merged = mergeDraftWithServer({ ...draft, plans: draft.plans }, plannedServer, frozen);
    return { ...merged, frozen, seenIds };
}

// The planner's single seed. A legacy note becomes a new id-less VA placed first, so it never
// interacts with seen ids or the server merge.
export function hydratePlans(input: HydratePlansInput): HydratedPlans {
    const merged = mergeSources(input);
    const converted = legacyNoteToDraft(input.legacyNote);
    if (converted === null) return { ...merged, convertedLegacyKey: null };
    return {
        ...merged,
        planned: [converted, ...merged.planned],
        convertedLegacyKey: converted.key
    };
}

export interface SavedNogoWindow {
    count: number;
    /** ISO start of the counting window; null if the server didn't send one. */
    since: string | null;
}

export type SavedNogoCounts = ReadonlyMap<string, SavedNogoWindow>;

// Running counts of the saved planned no-go VAs; a row switched to no-gos starts a new window.
export function savedNogoCounts(
    server: readonly VirtualAppointmentDto[] | undefined
): SavedNogoCounts {
    const counting = (server ?? []).filter((va) => !va.frozen && va.triggerKind === 'nogos');
    return new Map(
        counting.map((va) => [va.id, { count: va.nogoCount, since: va.nogoCountingSince ?? null }])
    );
}

// Submitting before the document's VAs load would send a list that omits them, and the
// backend deletes every planned VA a list omits - so anything but a loaded, valid list blocks.
export function resolveSubmitPlans(
    loadStatus: VirtualAppointmentLoadStatus,
    planned: VirtualAppointmentDraft[],
    errorsByKey: Readonly<Record<string, VirtualAppointmentDraftErrors>>
): SubmitPlansResult {
    if (loadStatus === 'loading') return { ok: false, blockedHint: LOADING_HINT };
    if (loadStatus === 'error') return { ok: false, blockedHint: LOAD_FAILED_HINT };
    const invalid = planned.filter((d) =>
        hasDraftErrors(errorsByKey[d.key] ?? validateDraft(d))
    ).length;
    if (invalid > 0) {
        const noun = invalid === 1 ? 'VA is' : 'VAs are';
        return { ok: false, blockedHint: `${invalid} ${noun} incomplete - fix or remove them` };
    }
    return { ok: true, plans: planned.map(draftToPlan) };
}

export type LastValidDocs = ReadonlyMap<string, TiptapDoc | null>;

// An over-cap description would make the whole draft save 400, so the row falls back to its
// last description under the cap (or none); the inline error keeps submit blocked meanwhile.
function toSavableDraftPlan(
    draft: VirtualAppointmentDraft,
    lastValidDocs: LastValidDocs
): VirtualAppointmentDraftPlan {
    if (!isDescriptionOverCap(draft.descriptionDoc)) return draftToDraftPlan(draft);
    const plan = draftToDraftPlan(draft);
    const fallback = lastValidDocs.get(draft.key) ?? null;
    return {
        ...plan,
        descriptionDoc: fallback
            ? draftToDraftPlan({ ...draft, descriptionDoc: fallback }).descriptionDoc
            : null
    };
}

// Before hydration the resumed draft is echoed; a legacy note rides along as its converted plan,
// since vcaNote is no longer sent (no seen ids come with it, so no deletion is ever inferred).
function echoBeforeHydration(draft: ResumedDraftPlans, legacyNote: string): DraftPlanFields {
    const converted = legacyNoteToDraft(legacyNote);
    const plans = converted ? [draftToDraftPlan(converted), ...(draft.plans ?? [])] : draft.plans;
    return {
        ...(plans !== undefined && { virtualAppointments: plans }),
        ...(draft.seenIds !== undefined && { virtualAppointmentsSeenIds: draft.seenIds })
    };
}

// Drafts keep every row as typed.
export function resolveDraftFields(
    hydrated: HydratedPlans | null,
    planned: VirtualAppointmentDraft[],
    draft: ResumedDraftPlans,
    lastValidDocs: LastValidDocs,
    legacyNote: string
): DraftPlanFields {
    if (hydrated === null) return echoBeforeHydration(draft, legacyNote);
    return {
        virtualAppointments: planned.map((d) => toSavableDraftPlan(d, lastValidDocs)),
        virtualAppointmentsSeenIds: hydrated.seenIds
    };
}

const VANISHED_DRAFT_HINT = 'This draft no longer exists. Save the draft again before submitting.';

interface VanishedDraftInput {
    planned: VirtualAppointmentDraft[];
    isEditingDocument: boolean;
    draftId: string | null;
}

// Rows with server ids only resolve through the edited document or the draft's group; with
// neither, the backend would 404. Saving the draft recreates it in that group.
export function vanishedDraftHint({
    planned,
    isEditingDocument,
    draftId
}: VanishedDraftInput): string | undefined {
    if (isEditingDocument || draftId !== null) return undefined;
    return planned.some((d) => d.id !== undefined) ? VANISHED_DRAFT_HINT : undefined;
}

export function plansSignature(drafts: VirtualAppointmentDraft[]): string {
    return JSON.stringify(drafts.map(({ key: _key, ...content }) => content));
}
